import { TRoadblockCaseData, TRoadblockCheckListItem, TRoadblockConfiguration } from "../roadblockTypesAndCodecs";
import {
    anyReceivedDocumentExists,
    clientsAuthorisedExchangeAndCompleteItem,
    sharedExchangeAndPreCompletionItems,
    sourceOfWealthSatisfiedItem,
} from "./SharedExchangeAndPreCompletionItems";
import { allDocumentSlotsReceived, anyDocSlotExists } from "./ProbateItems";
import { TCasesDocumentType } from "../../models/CaseDocumentType1";
import { clientName, clientsOfTypes, fact, formatEvidenceDate, neutralFact } from "../evidence/evidenceHelpers";
import { TRoadblockEvidenceLink } from "../../codecs/form/RoadblockForm";

const contractsLink: TRoadblockEvidenceLink = { block: "case_contracts", section: null };

const receivedDocDate = (c: TRoadblockCaseData, types: Array<TCasesDocumentType>): string | null =>
    c.cases_documents.reduce<string | null>(
        (latest, { type, received_date }) =>
            types.includes(type) && received_date !== null && (latest === null || received_date > latest)
                ? received_date
                : latest,
        null,
    );

const exchangeReadyItemsBeforeClientsAuthorised: Array<TRoadblockCheckListItem> = [
        {
            type: "all_enquiries_resolved_or_not_relevant",
            is_resolved_by_short_description: "All enquiries resolved or marked not relevant",
            create: { create_condition_description: "All cases", predicate: () => true },
            resolve: {
                is_resolved_by_long_description: `Every enquiry on the case being marked as resolved or not relevant.`,
                predicate: (c) =>
                    c.enquiries.every((e) => e.status === "resolved" || e.status === "not_relevant"),
                evidence: (c) => {
                    const total = c.enquiries.length;
                    const outstanding = c.enquiries.filter(
                        (e) => e.status !== "resolved" && e.status !== "not_relevant",
                    ).length;
                    return [
                        fact(
                            "Enquiries",
                            total === 0 ? "None raised" : `${total - outstanding} of ${total} resolved`,
                            outstanding === 0,
                        ),
                    ];
                },
            },
        },
        {
            type: "searches_required_answered",
            is_resolved_by_short_description: "Searches-required question answered",
            create: {
                create_condition_description: "Cash purchases",
                predicate: (c) => c.case.transaction_type === "purchase" && c.case.purchase_payment_method === "cash",
            },
            resolve: {
                is_resolved_by_long_description: `Recording whether searches are required on the case. "No" is an answer — the item resolves either way.`,
                predicate: (c) => c.case.searches_required !== null,
                evidence: (c) => [
                    fact(
                        "Searches required",
                        c.case.searches_required === null ? "Not answered" : c.case.searches_required ? "Yes" : "No",
                        c.case.searches_required !== null,
                    ),
                ],
            },
        },
        {
            type: "searches_ordered",
            is_resolved_by_short_description: "Searches ordered",
            create: {
                create_condition_description: "Purchase cases where searches are required and the dual-rep other side isn't buying them",
                predicate: (c) =>
                    c.case.transaction_type === "purchase"
                    && c.case.searches_required === true
                    && !c.dual_rep.other_side_is_purchasing_searches,
            },
            resolve: {
                is_resolved_by_long_description: `Ordering the search pack, which records the order date on the case.`,
                predicate: (c) => c.case.searches_ordered !== null,
                evidence: (c) => [
                    fact(
                        "Searches ordered",
                        c.case.searches_ordered !== null ? `Ordered ${formatEvidenceDate(c.case.searches_ordered)}` : "Not ordered",
                        c.case.searches_ordered !== null,
                    ),
                ],
            },
        },
        {
            type: "enquiries_report_uploaded",
            is_resolved_by_short_description: "Replies-to-enquiries report received",
            create: {
                create_condition_description: "Purchase cases only",
                predicate: (c) => c.case.transaction_type === "purchase",
            },
            resolve: {
                is_resolved_by_long_description: `A replies_to_enquiries_report document being uploaded and marked as received.`,
                predicate: (c) => anyReceivedDocumentExists(c, ["replies_to_enquiries_report"]),
                evidence: (c) => {
                    const received = receivedDocDate(c, ["replies_to_enquiries_report"]);
                    return [
                        fact(
                            "Replies to enquiries report",
                            received !== null ? `Received ${formatEvidenceDate(received)}` : "Not received",
                            received !== null,
                        ),
                    ];
                },
            },
        },

        ...sharedExchangeAndPreCompletionItems(),

        sourceOfWealthSatisfiedItem(true),

        {
            type: "gift_declaration_signed",
            is_resolved_by_short_description: "Gift Declaration received",
            create: {
                create_condition_description: "Purchase cases with at least one giftor on the case",
                predicate: (c) =>
                    c.case.transaction_type === "purchase"
                    && c.clients.some((client) => client.user_type === "giftor"),
            },
            resolve: {
                is_resolved_by_long_description: `Having a signed gift_declaration document uploaded against the case.`,
                predicate: (c) => anyReceivedDocumentExists(c, ["gift_declaration"]),
                evidence: (c) => {
                    const received = receivedDocDate(c, ["gift_declaration"]);
                    return [
                        ...clientsOfTypes(c.clients, ["giftor"]).map((client) =>
                            neutralFact(clientName(client), "Giftor"),
                        ),
                        fact(
                            "Gift Declaration",
                            received !== null ? `Received ${formatEvidenceDate(received)}` : "Not received",
                            received !== null,
                        ),
                    ];
                },
            },
        },
        {
            type: "buildings_insurance_received",
            is_resolved_by_short_description: "Buildings insurance received",
            create: {
                create_condition_description: "Purchase cases where the mortgage offer requires buildings insurance",
                predicate: (c) =>
                    c.case.transaction_type === "purchase"
                    && c.case.mortgage_offer_we_require_buildings_insurance === true,
            },
            resolve: {
                is_resolved_by_long_description: `Having a buildings_insurance document uploaded and marked as received.`,
                predicate: (c) => anyReceivedDocumentExists(c, ["buildings_insurance"]),
                evidence: (c) => {
                    const received = receivedDocDate(c, ["buildings_insurance"]);
                    return [
                        fact(
                            "Buildings insurance",
                            received !== null ? `Received ${formatEvidenceDate(received)}` : "Not received",
                            received !== null,
                        ),
                    ];
                },
            },
        },
        {
            type: "report_sent",
            is_resolved_by_short_description: "Report sent to client",
            create: {
                create_condition_description: "Purchase cases only (sale/transfer/remortgage don't send a client report)",
                predicate: (c) => c.case.transaction_type === "purchase",
            },
            resolve: {
                is_resolved_by_long_description: `Marking the case's reports_sent_to_client milestone as completed.`,
                predicate: (c) => c.case.reports_sent_to_client !== null,
                evidence: (c) => [
                    fact(
                        "Report to client",
                        c.case.reports_sent_to_client !== null
                            ? `Sent ${formatEvidenceDate(c.case.reports_sent_to_client)}`
                            : "Not sent",
                        c.case.reports_sent_to_client !== null,
                    ),
                ],
            },
        },
        {
            type: "ready_for_exchange_review_complete",
            is_resolved_by_short_description: "Ready-for-exchange review complete",
            create: { create_condition_description: "All cases", predicate: () => true },
            resolve: {
                is_resolved_by_long_description: `Marking the case as either having had its ready-for-exchange review, or as signed-off by an authoriser.`,
                predicate: (c) =>
                    c.case.is_ready_for_exchange_review !== null
                    || c.case.is_signed_off_by_authoriser_as_ready_for_exchange !== null,
                evidence: (c) => {
                    const reviewed = c.case.is_ready_for_exchange_review;
                    const signedOff = c.case.is_signed_off_by_authoriser_as_ready_for_exchange;
                    return [
                        fact(
                            "Ready-for-exchange review",
                            reviewed !== null
                                ? `Reviewed ${formatEvidenceDate(reviewed)}`
                                : signedOff !== null
                                ? `Authoriser sign-off ${formatEvidenceDate(signedOff)}`
                                : "Not reviewed",
                            reviewed !== null || signedOff !== null,
                        ),
                    ];
                },
            },
        },
        {
            type: "signed_contract_received",
            is_resolved_by_short_description: "Signed contract received",
            create: {
                create_condition_description: "Sale or purchase cases where a signed_contract slot has been created",
                predicate: (c) =>
                    (c.case.transaction_type === "sale" || c.case.transaction_type === "purchase")
                    && anyDocSlotExists(c, ["signed_contract"]),
            },
            resolve: {
                is_resolved_by_long_description: `Every signed_contract document slot on the case being uploaded and marked as received.`,
                predicate: (c) => allDocumentSlotsReceived(c, ["signed_contract"]),
                evidence: (c) => {
                    const received = allDocumentSlotsReceived(c, ["signed_contract"]);
                    const date = receivedDocDate(c, ["signed_contract"]);
                    return [
                        fact(
                            "Signed contract",
                            received && date !== null ? `Received ${formatEvidenceDate(date)}` : "Not received",
                            received,
                            contractsLink,
                        ),
                    ];
                },
            },
        },
];

export const ExchangeReady: TRoadblockConfiguration = {
    type: "exchange_ready",
    display_name: "Exchange Ready",
    only_create_if_resolved: [],
    requires_authoriser_signoff: true,
    blocks_exchange: true,
    create: {
        create_condition_description: "Instructed sale or purchase cases which are under offer",
        predicate: (c) =>
            c.case.is_instructed !== null
            && c.case.is_offer_accepted !== null
            && (c.case.transaction_type === "sale" || c.case.transaction_type === "purchase"),
    },
    checklist: [
        ...exchangeReadyItemsBeforeClientsAuthorised,
        clientsAuthorisedExchangeAndCompleteItem(exchangeReadyItemsBeforeClientsAuthorised),
    ],
};
